import { valueRender } from '@repo/toolkit/conditional-render'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import { useQuery } from '@tanstack/react-query'

import { getCenterLocalTransport } from '../../../../api/centers/get-center-local-transport'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { LocalTransportPricing } from '../../../../lib/types/models/local-transport-pricing/local-transport-pricing'
import CenterLocalTransportFooter from './center-local-transport-footer'

type Props = {
  centerId: number
  currency?: CurrencyEnum
  mode?: 'open' | 'validate' | 'view' | 'staff-view'
}

const CenterLocalTransportPricingTable = ({ centerId, currency, mode }: Props) => {
  const { t } = useTranslate()
  const { data: centerLocalTransport } = useQuery({
    queryKey: ['center-local-transport', centerId],
    queryFn: () => getCenterLocalTransport(centerId),
    enabled: !!centerId
  })

  const localTransportColumns: Column<LocalTransportPricing>[] = [
    {
      accessorKey: 'splitAge',
      header: t('ol.center.local.transport.table.split.age'),
      cell: ({ row }) => valueRender(row.original.splitAge)
    },
    {
      accessorKey: 'priceUnderSplitAge',
      header: `${t('ol.center.local.transport.table.price')} < ${centerLocalTransport?.splitAge ?? 'X'}`,
      cell: ({ row }) =>
        row.original.priceUnderSplitAge ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.priceUnderSplitAge, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'priceOverSplitAge',
      header: `${t('ol.center.local.transport.table.price')} > ${centerLocalTransport?.splitAge ?? 'X'}`,
      cell: ({ row }) =>
        row.original.priceOverSplitAge ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.priceOverSplitAge, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'numberOfFtrStudents',
      header: t('ol.center.local.transport.table.ftr.students')
    },
    {
      accessorKey: 'ftrAmount',
      header: t('ol.center.local.transport.table.ftr.amount'),
      cell: ({ row }) =>
        row.original.ftrAmount ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.ftrAmount, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'numberOfFrenchLeaders',
      header: t('ol.center.local.transport.table.french.leaders')
    },
    {
      accessorKey: 'frenchLeaderAmount',
      header: t('ol.center.local.transport.table.french.leaders.amount'),
      cell: ({ row }) =>
        row.original.frenchLeaderAmount ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.frenchLeaderAmount, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'totalCost',
      header: t('ol.center.local.transport.table.total.cost'),
      cell: ({ row }) =>
        row.original.totalCost ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.totalCost, currency)}
          />
        ) : (
          '-'
        )
    }
  ]
  return (
    <DataTable<LocalTransportPricing>
      columns={localTransportColumns}
      rowCount={1}
      mode="normal"
      data={centerLocalTransport ? [centerLocalTransport] : []}
      footer={<CenterLocalTransportFooter centerId={centerId} currency={currency} mode={mode} />}
      bodyVariant="white"
      footerRowClassName="bg-white-dark"
      paginated={false}
    />
  )
}

export default CenterLocalTransportPricingTable
